import type { EditTeamsMeetingOptions } from '@ms/yammer-data/dist/domains/editTeamsMeeting/actions.types';
import { getShouldShowMultipleEventUpdatesCarousel } from '@ms/yammer-data/dist/domains/pinnedThreads/teamsMeetingSelectors';
import {
  getTeamsMeetingId,
  getTeamsMeetingIdForReplyPublisher,
} from '@ms/yammer-data/dist/domains/publishers/selectors';
import {
  findTeamsMeetingById,
  findTeamsMeetingByIdThrows,
  findTeamsMeetingByOfficeMeetingId,
  getEditTeamsMeetingOptionsThrows,
  getOrganizersForTeamsMeetingId,
  getTeamsMeetingStatus,
  getViewerCanDelete,
  isAskMeAnythingLoadedInTeamsQnaApp,
} from '@ms/yammer-data/dist/domains/teamsMeetings/selectors';
import {
  findById as findThread,
  getTeamsMeetingIdForThreadThrows,
} from '@ms/yammer-data/dist/domains/threads/selectors';
import type { Publisher, TeamsMeeting, Thread } from '@ms/yammer-data/dist/state/types';
import { getApplication } from '@ms/yammer-libs-static-config';
import { getNextThirtyMinuteIntervalTime } from '@ms/yammer-web-support/dist/date';

import { useSelector } from '../useSelector';

type UseFindTeamsMeetingById = (teamsMeetingId: TeamsMeeting['id']) => ReturnType<typeof findTeamsMeetingById>;
/**
 * @deprecated PERFORMANCE This returns an entity object and will force re-renders whenever an API call etc. updates the entity.
 * This should be replaced with a `useTeamsMeetingField` selector.
 */
export const useFindTeamsMeetingById: UseFindTeamsMeetingById = (teamsMeetingId) =>
  useSelector((state) => findTeamsMeetingById(state, teamsMeetingId));

type UseFindTeamsMeetingThrows = (teamsMeetingId: TeamsMeeting['id']) => ReturnType<typeof findTeamsMeetingByIdThrows>;
/**
 * @deprecated PERFORMANCE This returns an entity object and will force re-renders whenever an API call etc. updates the entity.
 * This should be replaced with a `useTeamsMeetingFieldThrows` selector.
 */
export const useFindTeamsMeetingThrows: UseFindTeamsMeetingThrows = (teamsMeetingId) =>
  useSelector((state) => findTeamsMeetingByIdThrows(state, teamsMeetingId));

type UseTeamsMeetingStatus = (teamsMeetingId: TeamsMeeting['id']) => ReturnType<typeof getTeamsMeetingStatus>;
export const useTeamsMeetingStatus: UseTeamsMeetingStatus = (teamsMeetingId) =>
  useSelector((state) => getTeamsMeetingStatus(state, teamsMeetingId));

type UseTeamsMeetingIdByOfficeMeetingId = (
  officeMeetingId: TeamsMeeting['officeMeetingId']
) => TeamsMeeting['id'] | undefined;
/**
 * @deprecated PERFORMANCE This returns an entity object and will force re-renders whenever an API call etc. updates the entity.
 * This should be replaced with a `useTeamsMeetingIdByOfficeMeetingId` selector which returns a primitive id/string value, and
 * consumers can pass that id to a `useTeamsMeetingField` selector for each field they require.
 */
export const useTeamsMeetingIdByOfficeMeetingId: UseTeamsMeetingIdByOfficeMeetingId = (officeMeetingId) =>
  useSelector((state) => findTeamsMeetingByOfficeMeetingId(state, officeMeetingId)?.id);

type UseShouldShowMultipleEventUpdatesCarousel = (teamsMeetingId: TeamsMeeting['id']) => boolean;
export const useShouldShowMultipleEventUpdatesCarousel: UseShouldShowMultipleEventUpdatesCarousel = (teamsMeetingId) =>
  useSelector((state) => {
    const isTeamsQna = getApplication() === 'YAMMER_TEAMS_MEETING';

    if (isTeamsQna) {
      return false;
    }

    return getShouldShowMultipleEventUpdatesCarousel(state, teamsMeetingId);
  });

type UseIsCurrentUserTeamsMeetingAdmin = (
  teamsMeetingId: TeamsMeeting['id'] | undefined
) => TeamsMeeting['viewerIsAdmin'];
export const useIsCurrentUserTeamsMeetingAdmin: UseIsCurrentUserTeamsMeetingAdmin = (teamsMeetingId) =>
  useSelector((state) => {
    if (!teamsMeetingId) {
      return false;
    }

    return !!findTeamsMeetingById(state, teamsMeetingId)?.viewerIsAdmin;
  });

type UseIsCurrentUserTeamsMeetingAdminThrows = (teamsMeetingId: TeamsMeeting['id']) => TeamsMeeting['viewerIsAdmin'];
export const useIsCurrentUserTeamsMeetingAdminThrows: UseIsCurrentUserTeamsMeetingAdminThrows = (teamsMeetingId) =>
  useSelector((state) => findTeamsMeetingByIdThrows(state, teamsMeetingId).viewerIsAdmin);

type UseIsReplyToConversationEnabledThrows = (
  teamsMeetingId: TeamsMeeting['id']
) => TeamsMeeting['isReplyToConversationEnabled'];
export const useIsReplyToConversationEnabledThrows: UseIsReplyToConversationEnabledThrows = (teamsMeetingId) =>
  useSelector((state) => !!findTeamsMeetingByIdThrows(state, teamsMeetingId)?.isReplyToConversationEnabled);

type UseIsReplyToConversationEnabled = (
  teamsMeetingId: TeamsMeeting['id'] | undefined
) => TeamsMeeting['isReplyToConversationEnabled'];
export const useIsReplyToConversationEnabled: UseIsReplyToConversationEnabled = (teamsMeetingId) =>
  useSelector((state) => {
    if (!teamsMeetingId) {
      return true;
    }

    return !!findTeamsMeetingById(state, teamsMeetingId)?.isReplyToConversationEnabled;
  });

type UseViewerCanReplyThrows = (teamsMeetingId: TeamsMeeting['id']) => TeamsMeeting['viewerCanReply'];
export const useViewerCanReplyThrows: UseViewerCanReplyThrows = (teamsMeetingId) =>
  useSelector((state) => findTeamsMeetingByIdThrows(state, teamsMeetingId).viewerCanReply);

type UseViewerCanReply = (teamsMeetingId: TeamsMeeting['id'] | undefined) => TeamsMeeting['viewerCanReply'];
export const useViewerCanReply: UseViewerCanReply = (teamsMeetingId) =>
  useSelector((state) => {
    if (!teamsMeetingId) {
      return true;
    }

    return findTeamsMeetingByIdThrows(state, teamsMeetingId).viewerCanReply;
  });

type UseIsThreadStarterAdminModeratedThrows = (
  teamsMeetingId: TeamsMeeting['id']
) => TeamsMeeting['isThreadStarterAdminModerated'];
export const useIsThreadStarterAdminModeratedThrows: UseIsThreadStarterAdminModeratedThrows = (teamsMeetingId) =>
  useSelector((state) => findTeamsMeetingByIdThrows(state, teamsMeetingId).isThreadStarterAdminModerated);

type UseIsPrivateReplyEnabled = (threadId: Thread['id']) => TeamsMeeting['isPrivateReplyEnabled'];
export const useIsPrivateReplyEnabled: UseIsPrivateReplyEnabled = (threadId) =>
  useSelector((state) => {
    const thread = findThread(state, threadId);
    if (thread && !thread.viewerCanPrivateReply) {
      return false;
    }

    const teamsMeetingId = getTeamsMeetingIdForThreadThrows(state, threadId);
    if (!teamsMeetingId) {
      return false;
    }

    const teamsMeeting = findTeamsMeetingById(state, teamsMeetingId);
    if (!teamsMeeting) {
      return false;
    }

    return teamsMeeting.isPrivateReplyEnabled;
  });

type UseIsThreadStarterAdminModerated = (
  teamsMeetingId: TeamsMeeting['id'] | undefined
) => TeamsMeeting['isThreadStarterAdminModerated'];
export const useIsThreadStarterAdminModerated: UseIsThreadStarterAdminModerated = (teamsMeetingId) =>
  useSelector((state) => {
    if (!teamsMeetingId) {
      return false;
    }

    const teamsMeeting = findTeamsMeetingById(state, teamsMeetingId);

    if (!teamsMeeting) {
      return false;
    }

    return teamsMeeting.isThreadStarterAdminModerated;
  });

type UseHaveThreadStartersBeenAdminModerated = (
  teamsMeetingId: TeamsMeeting['id'] | undefined
) => TeamsMeeting['haveThreadStartersBeenAdminModerated'];
export const useHaveThreadStartersBeenAdminModerated: UseHaveThreadStartersBeenAdminModerated = (teamsMeetingId) =>
  useSelector((state) => {
    if (!teamsMeetingId) {
      return false;
    }

    const teamsMeeting = findTeamsMeetingById(state, teamsMeetingId);

    if (!teamsMeeting) {
      return false;
    }

    return teamsMeeting.haveThreadStartersBeenAdminModerated;
  });

type UseIsThreadStarterAdminOnlyThrows = (
  teamsMeetingId: TeamsMeeting['id']
) => TeamsMeeting['isThreadStarterAdminOnly'];
export const useIsThreadStarterAdminOnlyThrows: UseIsThreadStarterAdminOnlyThrows = (teamsMeetingId) =>
  useSelector((state) => !!findTeamsMeetingByIdThrows(state, teamsMeetingId)?.isThreadStarterAdminOnly);

type UseIsDiscussionPostingAdminOnly = (
  teamsMeetingId: TeamsMeeting['id'] | undefined
) => TeamsMeeting['isDiscussionPostingAdminOnly'];
export const useIsDiscussionPostingAdminOnly: UseIsDiscussionPostingAdminOnly = (teamsMeetingId) =>
  useSelector((state) => {
    if (!teamsMeetingId) {
      return false;
    }

    return !!findTeamsMeetingById(state, teamsMeetingId)?.isDiscussionPostingAdminOnly;
  });

type UseIsQuestionPostingAdminOnly = (
  teamsMeetingId: TeamsMeeting['id'] | undefined
) => TeamsMeeting['isQuestionPostingAdminOnly'];
export const useIsQuestionPostingAdminOnly: UseIsQuestionPostingAdminOnly = (teamsMeetingId) =>
  useSelector((state) => {
    if (!teamsMeetingId) {
      return false;
    }

    return !!findTeamsMeetingById(state, teamsMeetingId)?.isQuestionPostingAdminOnly;
  });

type UseIsHideAttendeeNamesEnabledThrows = (
  teamsMeetingId: TeamsMeeting['id']
) => TeamsMeeting['isHideAttendeeNamesEnabled'];
export const useIsHideAttendeeNamesEnabledThrows: UseIsHideAttendeeNamesEnabledThrows = (teamsMeetingId) =>
  useSelector((state) => findTeamsMeetingByIdThrows(state, teamsMeetingId).isHideAttendeeNamesEnabled);

type UseViewerCanStartThreadThrows = (teamsMeetingId: TeamsMeeting['id']) => TeamsMeeting['viewerCanStartThread'];
export const useViewerCanStartThreadThrows: UseViewerCanStartThreadThrows = (teamsMeetingId) =>
  useSelector((state) => findTeamsMeetingByIdThrows(state, teamsMeetingId).viewerCanStartThread);

type UseIsViewerAdminThrowsWithOptionalId = (teamsMeetingId?: TeamsMeeting['id']) => TeamsMeeting['viewerIsAdmin'];
export const useIsViewerAdminThrowsWithOptionalId: UseIsViewerAdminThrowsWithOptionalId = (teamsMeetingId) =>
  useSelector((state) => {
    if (!teamsMeetingId) {
      return false;
    }

    return findTeamsMeetingByIdThrows(state, teamsMeetingId).viewerIsAdmin;
  });

type UseEditTeamsMeetingOptionsThrows = (
  teamsMeetingId: TeamsMeeting['id']
) => ReturnType<typeof getEditTeamsMeetingOptionsThrows>;
/**
 * @deprecated PERFORMANCE This returns a new object instance and forces consumers to re-render whenever the
 * TeamsMeeting gets updated by an API call etc. Consumers should use `useTeamsMeetingFieldThrows` and select
 * the individual "edit" fields they require.
 */
export const useEditTeamsMeetingOptionsThrows: UseEditTeamsMeetingOptionsThrows = (teamsMeetingId) =>
  useSelector((state) => getEditTeamsMeetingOptionsThrows(state, teamsMeetingId));

type UseRealtimeChannelIdThrows = (teamsMeetingId: TeamsMeeting['id']) => TeamsMeeting['realtimeChannelId'];
export const useRealtimeChannelIdThrows: UseRealtimeChannelIdThrows = (teamsMeetingId) =>
  useSelector((state) => findTeamsMeetingByIdThrows(state, teamsMeetingId).realtimeChannelId);

type UseViewerCanDelete = (teamsMeetingId: TeamsMeeting['id']) => ReturnType<typeof getViewerCanDelete>;
export const useViewerCanDelete: UseViewerCanDelete = (teamsMeetingId) =>
  useSelector((state) => getViewerCanDelete(state, teamsMeetingId));

type UseIsAnonymousPostingEnabledByPublisherId = (
  publisherId: Publisher['id']
) => TeamsMeeting['isAnonymousPostingEnabled'];
export const useIsAnonymousPostingEnabled: UseIsAnonymousPostingEnabledByPublisherId = (publisherId) =>
  useSelector((state) => {
    const teamsMeetingId = getTeamsMeetingId(state, publisherId);

    if (!teamsMeetingId) {
      return false;
    }

    return findTeamsMeetingByIdThrows(state, teamsMeetingId).isAnonymousPostingEnabled;
  });

type UseIsAnonymousReplyEnabledByPublisherId = (
  publisherId: Publisher['id']
) => TeamsMeeting['isAnonymousReplyEnabled'];
export const useIsAnonymousReplyEnabled: UseIsAnonymousReplyEnabledByPublisherId = (publisherId) =>
  useSelector((state) => {
    const teamsMeetingId = getTeamsMeetingIdForReplyPublisher(state, publisherId);

    if (!teamsMeetingId) {
      return false;
    }

    return findTeamsMeetingByIdThrows(state, teamsMeetingId).isAnonymousReplyEnabled;
  });

type UseIsPrivateReplyEnabledByPublisherId = (publisherId: Publisher['id']) => TeamsMeeting['isPrivateReplyEnabled'];
export const useIsPrivateReplyEnabledByPublisherId: UseIsPrivateReplyEnabledByPublisherId = (publisherId) =>
  useSelector((state) => {
    const teamsMeetingId =
      getTeamsMeetingId(state, publisherId) ?? getTeamsMeetingIdForReplyPublisher(state, publisherId);

    if (!teamsMeetingId) {
      return false;
    }

    return findTeamsMeetingByIdThrows(state, teamsMeetingId).isPrivateReplyEnabled;
  });

type UseShouldShowTeamsQnAPostAsAnonymousUser = (publisherId: Publisher['id']) => boolean;
export const useShouldShowTeamsQnAPostAsAnonymousUser: UseShouldShowTeamsQnAPostAsAnonymousUser = (publisherId) => {
  const isAnonymousPostingEnabled = useIsAnonymousPostingEnabled(publisherId);
  const isAnonymousReplyEnabled = useIsAnonymousReplyEnabled(publisherId);

  return isAnonymousPostingEnabled || isAnonymousReplyEnabled;
};

type UseIsReactionEnabled = (threadId: Thread['id']) => TeamsMeeting['isReactionEnabled'];
export const useIsReactionEnabled: UseIsReactionEnabled = (threadId) =>
  useSelector((state) => {
    const teamsMeetingId = getTeamsMeetingIdForThreadThrows(state, threadId);

    if (!teamsMeetingId) {
      return true;
    }

    return !!findTeamsMeetingById(state, teamsMeetingId)?.isReactionEnabled;
  });

type UseTitleThrows = (teamsMeetingId: TeamsMeeting['id']) => TeamsMeeting['title'];
export const useTitleThrows: UseTitleThrows = (teamsMeetingId) =>
  useSelector((state) => findTeamsMeetingByIdThrows(state, teamsMeetingId).title);

type UseDescriptionThrows = (teamsMeetingId: TeamsMeeting['id']) => TeamsMeeting['description'];
export const useDescriptionThrows: UseDescriptionThrows = (teamsMeetingId) =>
  useSelector((state) => findTeamsMeetingByIdThrows(state, teamsMeetingId).description);

type UseStartAtThrows = (teamsMeetingId: TeamsMeeting['id']) => TeamsMeeting['startAt'];
export const useStartAtThrows: UseStartAtThrows = (teamsMeetingId) =>
  useSelector((state) => findTeamsMeetingByIdThrows(state, teamsMeetingId).startAt);

type UseEndAtThrows = (teamsMeetingId: TeamsMeeting['id']) => TeamsMeeting['endAt'];
export const useEndAtThrows: UseEndAtThrows = (teamsMeetingId) =>
  useSelector((state) => findTeamsMeetingByIdThrows(state, teamsMeetingId).endAt);

type UseEditTeamsMeetingOptionsOrDefaultThrows = (
  teamsMeetingId?: TeamsMeeting['id']
) => ReturnType<typeof getEditTeamsMeetingOptionsThrows>;
/**
 * @deprecated PERFORMANCE This returns a new object instance and forces consumers to re-render whenever the
 * TeamsMeeting gets updated by an API call etc. Consumers should use `useTeamsMeetingFieldThrows` and select
 * the individual "edit" fields they require.
 */
export const useEditTeamsMeetingOptionsOrDefaultThrows: UseEditTeamsMeetingOptionsOrDefaultThrows = (
  teamsMeetingId
) => {
  const minuteInMS = 1000 * 60;
  const startAt = getNextThirtyMinuteIntervalTime();
  const endAt = startAt + 60 * minuteInMS;

  return useSelector((state) => {
    if (!teamsMeetingId) {
      const teamsMeeting: EditTeamsMeetingOptions = {
        teamsMeetingId: '',
        title: '',
        description: '',
        startAt,
        endAt,
        isAnonymousPostingEnabled: false,
        isAnonymousReplyEnabled: false,
        isDiscussionPostingAdminOnly: false,
        isMeetingPrivateToInvitees: false,
        isQuestionPostingAdminOnly: false,
        isReplyToConversationEnabled: true,
        isThreadStarterAdminModerated: false,
        isThreadUpvoteEnabled: true,
        isNewQuestionNotificationEnabled: false,
        isReactionEnabled: false,
      };

      return teamsMeeting;
    }

    return getEditTeamsMeetingOptionsThrows(state, teamsMeetingId);
  });
};

type UseGetOrganizersForTeamsMeetingId = (
  teamsMeetingId: TeamsMeeting['id']
) => ReturnType<typeof getOrganizersForTeamsMeetingId>;
/**
 * @deprecated PERFORMANCE This returns a new array instance of User entity objects and forces re-renders whenever the
 * TeamsMeeting entity changes, and whenever ANY User entity in the entire system changes. This should be replaced by
 * a `useOrganizerIdsForTeamsMeeting` selector which returns a memoized list of user ids, then the top-level consuming
 * component can pass each id to a child component which will use `useUserField` to select only the specific fields it
 * requires while managing its own memoization to avoid unnecessary re-renders.
 */
export const useGetOrganizersForTeamsMeetingId: UseGetOrganizersForTeamsMeetingId = (teamsMeetingId) =>
  useSelector((state) => getOrganizersForTeamsMeetingId(state, teamsMeetingId));

type UseIsAskMeAnythingLoadedInTeamsQnaApp = () => boolean;
export const useIsAskMeAnythingLoadedInTeamsQnaApp: UseIsAskMeAnythingLoadedInTeamsQnaApp = () =>
  useSelector((state) => isAskMeAnythingLoadedInTeamsQnaApp(state));
